import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { StrategyChannel } from '@/types';
import { STRATEGY_CHART_COLORS, strategyChartTooltipProps, truncateChartLabel } from './strategy-chart-shared';

const MAX_CHANNELS = 8;

export interface StrategyChannelChartsProps {
  channels: StrategyChannel[];
  compact?: boolean;
}

export function StrategyChannelCharts({ channels, compact }: StrategyChannelChartsProps) {
  if (!channels.length) return null;

  const sorted = [...channels].sort((a, b) => b.priority - a.priority).slice(0, MAX_CHANNELS);

  const radarData = sorted.map((ch) => ({
    subject: truncateChartLabel(ch.name, compact ? 10 : 14),
    subjectFull: ch.name,
    Priorität: Math.min(100, Math.max(0, ch.priority * 10)),
    budget_pct: Math.min(100, Math.max(0, ch.budget_pct)),
  }));

  const barData = sorted.map((ch) => ({
    name: truncateChartLabel(ch.name, compact ? 16 : 22),
    nameFull: ch.name,
    budget_pct: ch.budget_pct,
  }));

  const radarH = compact ? 200 : 260;
  const barH = compact ? 180 : 220;

  return (
    <div className="grid w-full gap-6 lg:grid-cols-2">
      <div className="min-w-0" style={{ height: radarH }}>
        <p className="mb-1 text-xs font-medium text-muted">
          Kanal-Fokus (Priorität & Budget-Anteil)
        </p>
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart
            cx="50%"
            cy="50%"
            outerRadius={compact ? '68%' : '72%'}
            data={radarData}
            margin={compact ? { top: 8, right: 8, bottom: 8, left: 8 } : { top: 12, right: 24, bottom: 12, left: 24 }}
          >
            <PolarGrid className="stroke-primary-foreground dark:stroke-muted" />
            <PolarAngleAxis
              dataKey="subject"
              tick={{ fontSize: compact ? 9 : 11 }}
              className="fill-muted "
            />
            <PolarRadiusAxis
              angle={30}
              domain={[0, 100]}
              tick={{ fontSize: compact ? 9 : 10 }}
              className="fill-muted "
            />
            <Tooltip
              {...strategyChartTooltipProps}
              formatter={(value: number, name: string) => [`${value}%`, name]}
              labelFormatter={(_, payload) => {
                const row = payload?.[0]?.payload as { subjectFull?: string; subject?: string } | undefined;
                return row?.subjectFull ?? row?.subject ?? '';
              }}
            />
            <Legend wrapperStyle={{ fontSize: compact ? 10 : 12 }} />
            <Radar
              name="Priorität (×10)"
              dataKey="Priorität"
              stroke={STRATEGY_CHART_COLORS[0]}
              fill={STRATEGY_CHART_COLORS[0]}
              fillOpacity={0.35}
            />
            <Radar
              name="Budget %"
              dataKey="budget_pct"
              stroke={STRATEGY_CHART_COLORS[2]}
              fill={STRATEGY_CHART_COLORS[2]}
              fillOpacity={0.22}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      <div className="min-w-0" style={{ height: barH }}>
        <p className="mb-1 text-xs font-medium text-muted">Budget-Anteil nach Kanal</p>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={barData}
            margin={
              compact
                ? { top: 4, right: 8, left: 4, bottom: 4 }
                : { top: 8, right: 16, left: 8, bottom: 8 }
            }
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-primary-foreground dark:stroke-muted" horizontal={false} />
            <XAxis
              type="number"
              domain={[0, 'auto']}
              tick={{ fontSize: compact ? 10 : 11 }}
              className="fill-muted "
              tickFormatter={(v) => `${v}%`}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={compact ? 88 : 120}
              tick={{ fontSize: compact ? 9 : 11 }}
              className="fill-muted "
            />
            <Tooltip
              {...strategyChartTooltipProps}
              formatter={(value: number) => [`${value}%`, 'Budget']}
              labelFormatter={(_, payload) => {
                const row = payload?.[0]?.payload as { nameFull?: string; name?: string } | undefined;
                return row?.nameFull ?? row?.name ?? '';
              }}
            />
            <Bar
              dataKey="budget_pct"
              name="Budget %"
              fill={STRATEGY_CHART_COLORS[1]}
              radius={[0, 4, 4, 0]}
              maxBarSize={compact ? 14 : 20}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
